import React from 'react';
import { useNavigate } from 'react-router-dom';

import { 
  FaMicrochip, 
  FaBrain, 
  FaBuilding, 
  FaBolt, 
  FaVrCardboard,
  FaHeadSideVirus,
  FaRobot,
  FaFlask,
  FaSatelliteDish,
  FaNetworkWired,
  FaIoxhost,
  FaSolarPanel
} from 'react-icons/fa';

const FutureTechPage = () => {
  const navigate = useNavigate();

  const technologies = [
    {
      id: 1,
      title: "Quantum Computing",
      description: "Revolutionizing computational power with quantum mechanics principles, enabling unprecedented processing capabilities for complex problem-solving and cryptographic applications.",
      icon: <FaMicrochip />,
      gradientClass: "quantum-gradient",
      iconColor: "#3b82f6",
      path: "/quantum-computing"
    },
    {
      id: 2,
      title: "Space Technologies",
      description: "Advancing space exploration and satellite technologies, developing cutting-edge solutions for communication, navigation, and deep space missions.",
      icon: <FaSatelliteDish />,
      gradientClass: "space-gradient",
      iconColor: "#8b5cf6",
      path: "/space-technologies"
    },
    {
      id: 3,
      title: "Renewable Energy",
      description: "Creating sustainable energy solutions through solar, wind, and innovative clean technologies to build a greener future for generations to come.",
      icon: <FaSolarPanel />,
      gradientClass: "energy-gradient",
      iconColor: "#10b981",
      path: "/renewable-energy"
    },
    {
      id: 4,
      title: "Blockchain Technologies",
      description: "Developing secure, decentralized systems for finance, supply chain, and digital identity management with transparent and immutable ledger solutions.",
      icon: <FaNetworkWired />,
      gradientClass: "blockchain-gradient",
      iconColor: "#f59e0b",
      path: "/blockchain-technologies"
    },
    {
      id: 5,
      title: "Multi-Agent AI Models",
      description: "Building intelligent systems with multiple AI agents working collaboratively to solve complex enterprise challenges and automate business processes.",
      icon: <FaBrain />,
      gradientClass: "ai-gradient",
      iconColor: "#06b6d4",
      path: "/multi-agent-ai"
    },
    {
      id: 6,
      title: "Enterprise Applications",
      description: "Developing scalable business solutions that streamline operations, enhance productivity, and drive digital transformation across industries.",
      icon: <FaBuilding />,
      gradientClass: "enterprise-gradient",
      iconColor: "#6366f1",
      path: "/enterprise-applications"
    },
    {
      id: 7,
      title: "Energy Sector Solutions",
      description: "Innovating smart grid technologies, energy storage systems, and efficient power distribution networks for the modern energy landscape.",
      icon: <FaBolt />,
      gradientClass: "sector-gradient",
      iconColor: "#eab308",
      path: "/energy-sector"
    },
    {
      id: 8,
      title: "IoT & Connected Systems",
      description: "Creating interconnected device ecosystems that enable smart cities, industrial automation, and seamless data exchange across platforms.",
      icon: <FaIoxhost/>,
      gradientClass: "iot-gradient",
      iconColor: "#059669",
      path: "/iot-connected-systems"
    },
    {
      id: 9,
      title: "Augmented Reality",
      description: "Developing immersive AR experiences that blend digital content with the real world, transforming education, healthcare, manufacturing, and entertainment industries.",
      icon: <FaVrCardboard />,
      gradientClass: "ar-gradient",
      iconColor: "#ec4899",
      path: "/augmented-reality"
    },
    {
      id: 10,
      title: "Neural Interfaces",
      description: "Creating brain-computer interfaces that enable direct communication between the human brain and digital devices, revolutionizing healthcare and human-computer interaction.",
      icon: <FaHeadSideVirus />,
      gradientClass: "neural-gradient",
      iconColor: "#f97316",
      path: "/neural-interfaces"
    },
    {
      id: 11,
      title: "Robotics & Automation",
      description: "Designing advanced robotic systems and automation solutions for manufacturing, healthcare, logistics, and service industries with AI-powered decision making.",
      icon: <FaRobot />,
      gradientClass: "robotics-gradient",
      iconColor: "#7136d7",
      path: "/robotics-automation"
    },
    {
      id: 12,
      title: "AI-Powered Scientific Research",
      description: "Revolutionizing scientific discovery through AI applications in biology, chemistry, physics, drug discovery, climate modeling, and agricultural innovation for breakthrough research.",
      icon: <FaFlask />,
      gradientClass: "science-gradient",
      iconColor: "#14b8a6",
      path: "/ai-scientific-research"
    }
  ];

  const getButtonClass = (index) => {
    const classes = [
      'quantum-btn', 'space-btn', 'energy-btn', 'blockchain-btn', 
      'ai-btn', 'enterprise-btn', 'sector-btn', 'iot-btn', 
      'ar-btn', 'neural-btn', 'robotics-btn', 'science-btn'
    ];
    return classes[index];
  };

  // Fixed navigation function - was using technologies.path instead of tech.path
  const handleNavigation = (path) => {
    navigate(path);
  };
  
  return (
    <div className="future-tech-container">
      <style jsx>{`
        .future-tech-container {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
          min-height: 100vh;
          padding: 60px 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
          margin-top: 40px;
        }
        
        .tech-card {
          background: #000000;
          backdrop-filter: blur(10px);
          border: 1px solid #006aff;
          border-radius: 20px;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .tech-card:hover {
          transform: translateY(-15px) scale(1.02);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
          border-color: rgba(148, 163, 184, 0.6);
        }
        
        .tech-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0;
          transition: opacity 0.4s ease;
          border-radius: 20px;
        }
        
        .tech-card:hover::before {
          opacity: 0.1;
        }
        
        .quantum-gradient::before { background: linear-gradient(135deg, #3b82f6, #8b5cf6); }
        .space-gradient::before { background: linear-gradient(135deg, #8b5cf6, #ec4899); }
        .energy-gradient::before { background: linear-gradient(135deg, #10b981, #0d9488); }
        .blockchain-gradient::before { background: linear-gradient(135deg, #f59e0b, #ef4444); }
        .ai-gradient::before { background: linear-gradient(135deg, #06b6d4, #3b82f6); }
        .enterprise-gradient::before { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
        .sector-gradient::before { background: linear-gradient(135deg, #eab308, #f59e0b); }
        .iot-gradient::before { background: linear-gradient(135deg, #059669, #10b981); }
        .ar-gradient::before { background: linear-gradient(135deg, #ec4899, #be185d); }
        .neural-gradient::before { background: linear-gradient(135deg, #f97316, #ea580c); }
        .robotics-gradient::before { background: linear-gradient(135deg, #7136d7, #5a8ae2); }
        .science-gradient::before { background: linear-gradient(135deg, #14b8a6, #0f766e); }
        
        .tech-card-body {
          padding: 30px;
          display: flex;
          flex-direction: column;
          text-align: center;
          position: relative;
          z-index: 1;
          flex: 1;
        }
        
        .tech-icon {
          font-size: 4rem;
          margin: 0 0 20px 0;
          transition: all 0.3s ease;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .tech-card:hover .tech-icon {
          transform: scale(1.15) rotate(5deg);
        }
        
        .tech-title {
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 15px;
          transition: all 0.3s ease;
          line-height: 1.3;
        }
        
        .tech-card:hover .tech-title {
          background: linear-gradient(45deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .tech-description {
          color: #cbd5e1;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 25px;
          flex: 1;
        }
        
        .tech-button {
          border-radius: 12px;
          padding: 14px 28px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          border: none;
          width: 100%;
          color: white;
          cursor: pointer;
          font-size: 0.9rem;
          margin-top: auto;
        }
        
        .tech-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
        }
        
        .tech-button:active {
          transform: translateY(-1px);
        }
        
        .quantum-btn { background: linear-gradient(45deg, #3b82f6, #8b5cf6); }
        .space-btn { background: linear-gradient(45deg, #8b5cf6, #ec4899); }
        .energy-btn { background: linear-gradient(45deg, #10b981, #0d9488); }
        .blockchain-btn { background: linear-gradient(45deg, #f59e0b, #ef4444); }
        .ai-btn { background: linear-gradient(45deg, #06b6d4, #3b82f6); }
        .enterprise-btn { background: linear-gradient(45deg, #6366f1, #8b5cf6); }
        .sector-btn { background: linear-gradient(45deg, #eab308, #f59e0b); }
        .iot-btn { background: linear-gradient(45deg, #059669, #10b981); }
        .ar-btn { background: linear-gradient(45deg, #ec4899, #be185d); }
        .neural-btn { background: linear-gradient(45deg, #f97316, #ea580c); }
        .robotics-btn { background: linear-gradient(45deg, #7136d7, #5a8ae2); }
        .science-btn { background: linear-gradient(45deg, #14b8a6, #0f766e); }
        
        .main-title {
          color: #1e293b;
          font-size: 3.5rem;
          font-weight: 800;
          text-align: center;
          margin-bottom: 20px;
          background: linear-gradient(45deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }
        
        .main-subtitle {
          color: #475569;
          font-size: 1.3rem;
          text-align: center;
          margin-bottom: 60px;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
          font-weight: 400;
        }
        
        @media (max-width: 768px) {
          .future-tech-container {
            padding: 40px 0;
          }
          
          .container {
            padding: 0 15px;
          }
          
          .main-title {
            font-size: 2.5rem;
          }
          
          .main-subtitle {
            font-size: 1.1rem;
            padding: 0 10px;
          }
          
          .tech-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .tech-icon {
            font-size: 3rem;
          }
          
          .tech-title {
            font-size: 1.3rem;
          }
          
          .tech-card-body {
            padding: 25px;
          }
        }
        
        @media (max-width: 480px) {
          .main-title {
            font-size: 2rem;
          }
          
          .main-subtitle {
            font-size: 1rem;
          }
          
          .tech-card-body {
            padding: 20px;
          }
          
          .tech-button {
            padding: 12px 24px;
            font-size: 0.85rem;
          }
        }
        
        @media (min-width: 1200px) {
          .tech-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        
        @media (min-width: 992px) and (max-width: 1199px) {
          .tech-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        
        @media (min-width: 768px) and (max-width: 991px) {
          .tech-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
      <div className="container">
        <div>
          <h1 className="main-title">Future-Driven Innovation & Technologies</h1>
          <p className="main-subtitle">
            Pioneering tomorrow's innovations today. Explore cutting-edge technologies that are reshaping industries and creating new possibilities for the future.
            <br/><br/>
            We are actively working on high-demand emerging technologies like Quantum Computing, AI, Blockchain, SpaceTech, and Renewable Energy — shaping the future with real-world, scalable solutions.
          </p>
        </div>
        
        <div className="tech-grid">
          {technologies.map((tech, index) => (
            <div key={tech.id} className={`tech-card ${tech.gradientClass}`}>
              <div className="tech-card-body">
                <div className="tech-icon" style={{ color: tech.iconColor }}>
                  {tech.icon}
                </div>
                
                <h3 className="tech-title">{tech.title}</h3>
                
                <p className="tech-description">{tech.description}</p>
                
                <button 
                  className={`tech-button ${getButtonClass(index)}`}
                  onClick={() => handleNavigation(tech.path)}
                >
                  Learn More
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FutureTechPage;